import * as React from 'react';
import { Dropdown, Form, InputGroup } from 'react-bootstrap';

interface Option {
  value: string | number;
  label: string;
}

interface SearchableSingleSelectDropdownProps {
  name: string;
  disabled?: boolean;
  fullWidth?: boolean;
  label: string;
  value?: string | number | null;
  variant?: string;
  onChange?: (
    event: React.ChangeEvent<HTMLSelectElement>,
    name: string,
    value: string | number | null,
  ) => void;
  options?: Option[];
  error?: string;
  touched?: boolean;
  required?: boolean;
  selectOption?: boolean;
}

const SearchableSingleSelectDropdown: React.FC<
  SearchableSingleSelectDropdownProps
> = ({
  name,
  disabled = false,
  fullWidth = false,
  label,
  value = null,
  variant,
  onChange,
  options = [],
  error,
  touched,
  required = false,
  selectOption = false,
}) => {
    const [selectedValue, setSelectedValue] = React.useState<
      string | number | null
    >(value);
    const [searchQuery, setSearchQuery] = React.useState<string>('');
    const [activeDropdownLabel, setActiveDropdownLabel] = React.useState<
      string | null
    >(null);

    const dropdownMenuId = `dropdown-menu-${name}`;

    const handleSelect = (selected: string | number) => {
      setSelectedValue(selected);
      setActiveDropdownLabel(null);
      setSearchQuery(''); // Clear search query after selection
      if (onChange) {
        const event = {
          target: { name, value: selected },
        } as React.ChangeEvent<HTMLSelectElement>;
        onChange(event, name, selected);
      }
    };

    const renderSelectedValue = (selected: string | number | null) => {
      if (!selected) {
        return 'Select';
      }
      const selectedClient = options?.find((client) => client.value === selected);
      return selectedClient?.label || 'Select';
    };

    const dropdownOptions = selectOption
      ? [{ value: '', label: 'Select' }, ...options]
      : options;

    const filteredData = dropdownOptions?.filter(
      (item) =>
        item.label &&
        item.label.toLowerCase().includes(searchQuery.toLowerCase()),
    );

    React.useEffect(() => {
      setSelectedValue(value);
    }, [value]);

    const handleDropdownToggle = (isOpen: boolean) => {
      setActiveDropdownLabel(isOpen ? label : null);
      if (!isOpen) {
        setSearchQuery('');
      }
    };

    // ✅ Scroll listener to close dropdown if user scrolls outside
    React.useEffect(() => {
      const handleScroll = (event: Event) => {
        const dropdownMenu = document.getElementById(dropdownMenuId);
        if (dropdownMenu && dropdownMenu.contains(event.target as Node)) {
          return; // Don't close if scrolling inside the dropdown
        }
        if (activeDropdownLabel === label) {
          setActiveDropdownLabel(null); // Close dropdown
        }
      };

      window.addEventListener('scroll', handleScroll, true);

      return () => {
        window.removeEventListener('scroll', handleScroll, true);
      };
    }, [activeDropdownLabel, label]);

    return (
      <Form.Group
        controlId='singleSelectDropdown'
        className='labelDropdownWrapper bg-white '
        style={{ width: fullWidth ? '100%' : 'auto' }}
      >
        <Form.Label
          onClick={() => {
            if (!disabled) {
              document.getElementById(`dropdown-toggle-${name}`)?.click();
            }
          }}
          style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
        >
          {label} {!disabled && required && <span className='required'>*</span>}
        </Form.Label>

        <Dropdown
          className={`dropdownStatic ${touched && error ? 'is-invalid' : ''}`}
          autoClose='outside'
          onToggle={handleDropdownToggle}
          show={activeDropdownLabel === label}
        >
          <Dropdown.Toggle
            id={`dropdown-toggle-${name}`}
            variant={variant || 'outline-secondary'}
            disabled={disabled}
            className={`w-100 text-start ${touched && error ? 'is-invalid' : ''}`}
          >
            {renderSelectedValue(selectedValue)}
          </Dropdown.Toggle>

          <Dropdown.Menu id={dropdownMenuId}>
            <InputGroup className='p-2'>
              <Form.Control
                type='text'
                placeholder='Search'
                value={searchQuery}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setSearchQuery(e.target.value)
                }
              />
              <span className='material-icons material-symbols-outlined multiselect_iconSearch'>
                search
              </span>
              <span
                className='material-icons material-symbols-outlined multiselect_iconclose'
                onClick={() => setSearchQuery('')}
                style={{ cursor: 'pointer' }}
              >
                close
              </span>
            </InputGroup>

            {filteredData?.length > 0 ? (
              filteredData.map((item) => (
                <Dropdown.Item
                  key={item.value}
                  as='div'
                  className='d-flex align-items-center px-2 py-2'
                  onClick={() => handleSelect(item.value)}
                  style={{ cursor: 'pointer' }}
                >
                  <Form.Check
                    type='radio'
                    className='me-2'
                    checked={selectedValue === item.value}
                    onChange={() => handleSelect(item.value)}
                    disabled={disabled}
                  />
                  <span className='ellipses_label' title={String(item.label)}>
                    {item.label}
                  </span>
                </Dropdown.Item>
              ))
            ) : (
              <Dropdown.Item disabled>No Data Found</Dropdown.Item>
            )}
          </Dropdown.Menu>
        </Dropdown>
      </Form.Group>
    );
  };

export default SearchableSingleSelectDropdown;
